import {
  createTaskSchema,
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  type TaskDetails,
  type TaskPriority,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Dialog } from '@/components/dialog';
import { toLocalInput } from '@/components/task-bits';
import { Button, Field, Input, Select } from '@/components/ui';
import { UserPicker } from '@/components/user-picker';
import { ApiError, api } from '@/lib/api';
import { useUser } from '@/lib/auth';
import { projectsQuery } from '@/lib/queries';

/**
 * Создание и изменение задачи. Для подзадачи передаётся parent — проект подставится от родителя.
 * Поля проверяются той же схемой, что и на сервере.
 */
export function TaskFormDialog({
  task,
  parent,
  defaultProjectId,
  onClose,
}: {
  task?: TaskDetails;
  parent?: { id: string; number: number; title: string };
  defaultProjectId?: string;
  onClose: () => void;
}) {
  const me = useUser();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const projects = useQuery(projectsQuery);
  const [assignee, setAssignee] = useState<string[]>([task?.assignee.userId ?? me.id]);
  const [coexecutors, setCoexecutors] = useState<string[]>(task?.coexecutors.map((c) => c.userId) ?? []);
  const [observers, setObservers] = useState<string[]>(task?.observers.map((c) => c.userId) ?? []);
  const [showMore, setShowMore] = useState(
    Boolean(task && (task.coexecutors.length || task.observers.length)),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>): Promise<{ id?: string }> =>
      task
        ? api<{ ok: true }>(`/tasks/${task.id}`, { method: 'PATCH', body }).then(() => ({}))
        : api<{ id: string }>('/tasks', { method: 'POST', body }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['tasks'] });
      onClose();
      if (!task && res.id && !parent) await navigate({ to: '/tasks/$id', params: { id: res.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length ? null : err.message);
      }
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const deadline = String(fd.get('deadline') || '');
    const checklist = String(fd.get('checklist') ?? '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    const body = {
      title: String(fd.get('title') ?? ''),
      description: String(fd.get('description') ?? ''),
      assigneeId: assignee[0],
      coexecutorIds: coexecutors,
      observerIds: observers,
      projectId: (fd.get('projectId') as string) || null,
      deadline: deadline ? new Date(deadline).toISOString() : null,
      priority: fd.get('priority') as TaskPriority,
      requireReview: fd.get('requireReview') === 'on',
      ...(task ? {} : { checklist, parentId: parent?.id ?? null }),
    };
    const parsed = createTaskSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    save.mutate(parsed.data);
  }

  const title = task ? `Задача #${task.number}` : parent ? `Подзадача к #${parent.number}` : 'Новая задача';
  const activeProjects = projects.data?.filter((p) => !p.isArchived) ?? [];

  return (
    <Dialog title={title} onClose={onClose} width={680}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Что сделать" error={errors.title}>
          {(p) => (
            <Input
              {...p}
              name="title"
              defaultValue={task?.title}
              placeholder="Например: подготовить стенд 001-700 к отгрузке"
            />
          )}
        </Field>
        <Field label="Подробности" error={errors.description}>
          {(p) => (
            <textarea
              {...p}
              name="description"
              rows={4}
              defaultValue={task?.description}
              className="input h-auto py-2"
              placeholder="Что важно знать исполнителю"
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Исполнитель" error={errors.assigneeId}>
            {(p) => (
              <UserPicker id={p.id} invalid={p['aria-invalid']} value={assignee} onChange={setAssignee} />
            )}
          </Field>
          <Field label="Срок" error={errors.deadline}>
            {(p) => (
              <Input
                {...p}
                name="deadline"
                type="datetime-local"
                defaultValue={toLocalInput(task?.deadline)}
              />
            )}
          </Field>
          <Field label="Проект" error={errors.projectId}>
            {(p) => (
              <Select
                {...p}
                name="projectId"
                defaultValue={task?.project?.id ?? defaultProjectId ?? ''}
                disabled={Boolean(parent)}
              >
                <option value="">{parent ? 'Как у родительской задачи' : 'Без проекта'}</option>
                {activeProjects.map((pr) => (
                  <option key={pr.id} value={pr.id}>
                    {pr.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Приоритет" error={errors.priority}>
            {(p) => (
              <Select {...p} name="priority" defaultValue={task?.priority ?? 'normal'}>
                {TASK_PRIORITIES.map((pr) => (
                  <option key={pr} value={pr}>
                    {TASK_PRIORITY_LABELS[pr]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            name="requireReview"
            defaultChecked={task?.requireReview}
            className="mt-0.5 size-4 accent-[var(--color-graphite)]"
          />
          <span>
            Проверить результат
            <span className="block text-[12.5px] text-steel">
              Выполненная задача придёт вам на проверку, а не закроется сразу
            </span>
          </span>
        </label>

        {showMore ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Соисполнители" error={errors.coexecutorIds}>
              {(p) => <UserPicker id={p.id} multiple value={coexecutors} onChange={setCoexecutors} />}
            </Field>
            <Field label="Наблюдатели" error={errors.observerIds}>
              {(p) => <UserPicker id={p.id} multiple value={observers} onChange={setObservers} />}
            </Field>
          </div>
        ) : (
          <button type="button" className="link self-start text-[13.5px]" onClick={() => setShowMore(true)}>
            Добавить соисполнителей и наблюдателей
          </button>
        )}

        {!task && (
          <Field label="Чек-лист" hint="Каждый пункт с новой строки" error={errors.checklist}>
            {(p) => <textarea {...p} name="checklist" rows={3} className="input h-auto py-2" />}
          </Field>
        )}

        {formError && (
          <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-[13.5px] text-danger">
            {formError}
          </p>
        )}
        <div className="flex gap-2 border-t border-line pt-4">
          <Button type="submit" loading={save.isPending}>
            {task ? 'Сохранить' : 'Поставить задачу'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
